import { IATA } from '../iata';

export namespace Qatar {
  export enum ProductCode {
    GCR = 'GCR', // Qatar General
    QRDG = 'QRDG', // Qatar DGR
    PHARM = 'PHARM', // Qatar Pharma Care
    SLIFT = 'SLIFT', // Qatar SecureLift
    QFR = 'QFR', // Qatar Perishables
    SEM = 'SEM', // Qatar Semiconductors

    XPS = 'XPS', // GCR Product, but expeditated under Q-Plus
    VIC = 'VIC', // GCR Product, but expeditated under Q-Prime
  }

  /**
   * Healthcheck endpoint that will be triggered from an alert
   *  */
  export type GET_STATUS = {
    responses?: {
      /**
       * Models the standard structure for an error response
       */
      /**
       *
       * @example
       * {
       *   "success": false,
       *   "apiName": "api-name",
       *   "version": "v1",
       *   "correlationId": "3sd3-213dhusa-21313213231",
       *   "timestamp": "2020-08-06T15:49:57.819Z",
       *   "errorDetails": [
       *     {
       *       "code": 404,
       *       "message": "The API has not been implemented",
       *       "additionalInfo": "Not Found : The API has not been implemented"
       *     }
       *   ]
       * }
       */
      /**
       * The origin server did not find a current representation for the target resource or is not willing to disclose that one exists.
       */
      404?: {
        success: boolean;
        apiName: string;
        version: string;
        correlationId: string;
        timestamp: string;
        errorDetails: {
          code: number;
          message: string;
          additionalInfo: string;
        }[];
      };
    };
  };
  /**
   * Availability and Rate Request API service provides the ‘flight availability’ and associated ‘rate information’ to the customer. Availability and Rate API provides a real time visibility of available flight capacity and rate details. So that, customers can check the options instantly and decide based on the current availability rather than waiting for Airline response through asynchronous or manual communication channels
   *  */
  export type POST_AVAILABILITY = {
    body?: {
      /**
       * The customer information should be provided. Here agentCassCode and agentIataCode are mandatory fields and   accountNumber is conditionally mandatory only when IATA or CASS not exists. Also the phone number accepts 0-9, Space< >, Plus<+> and Round Bracket<()>,Mobile number allow 0-9, Space< >, Plus<+> and Round Bracket<()>
       * @example
       * {
       *   "accountNumber": "7678522",
       *   "agentCassCode": "6815",
       *   "agentIataCode": "2347041",
       *   "agentCode": "4783457",
       *   "contactDetails": {
       *     "emailAddress": "test@qatarairways.com.qa",
       *     "name": "Nishi Sahoo",
       *     "phoneNumber": "+9473445435435"
       *   },
       *   "customerName": "XForwarding"
       * }
       */
      customerDetails?: {
        /**
         * The acccount number of the customer will be mentioned. This is conditionally mandatory field and Mandatory only when IATA or CASS not exists
         * @example
         * "accountNumber - 7678522"
         */
        accountNumber?: string;
        /**
         * The agent IATA cass code to be mentioned, only number characters are allowed
         * @example
         * "agentCassCode - 2883"
         */
        agentCassCode?: string | number;
        /**
         * The agent IATA  code to be mentioned, only number characters are allowed
         * @example
         * "agentCassCode - 8324557"
         */
        agentIataCode?: string | number;
        /**
         * The agent code to be mentioned
         * @example
         * "agentCode - 4809248"
         */
        agentCode?: string;
        /**
         * Contact details of the customer will be mentioned, including emailAddress, Agent name and phoneNumber
         */
        contactDetails?: {
          emailAddress?: string;
          name?: string;
          phoneNumber?: string;
        };
        /**
         * The agent name to be mentioned. which is an optional field.
         */
        customerName?: string;
      };
      /**
       * Destination of the AWB which need to be booked
       * @example
       * "destination : HKG"
       */
      destination: string;
      /**
       * Destination type of the AWB which need to be booked whether it is an airport, city or station
       * @example
       * "destinationType : airport"
       */
      destinationType: string;
      /**
       * Origin of the AWB which need to be booked
       * @example
       * "origin : DOH"
       */
      origin: string;
      /**
       * Origin type of the AWB which need to be booked whether it is an airport, city or station
       * @example
       * "originType : airport"
       */
      originType: string;

      documentNumber?: string;
      /**
       * The AWB product details to be mentioned here. CommodityCode and ProductCode are mandatory fields
       * @example
       * {
       *   "productCode": "GCR",
       *   "serviceCode": "J",
       *   "commodityCode": 9999,
       *   "natureOfGoods": "GENERAL",
       *   "dgShipment": false,
       *   "caoIndicator": false,
       *   "specialHandlingCodes": [
       *     "GCR"
       *   ],
       *   "densityGroup": ""
       * }
       */
      productDetails?: {
        /**
         * Optional field to indicate whether the shipment is Cargo aircraft only or not. Expected True/False
         */
        caoIndicator?: boolean;
        /**
         * Commodity code of the shipment, currently commodity codes of General shipment is expected
         * @example
         * 9998
         */
        commodityCode: string | number;
        /**
         * To indicate whether the shipment is DG or not. Expected True/False
         */
        dgShipment?: boolean;
        /**
         * To mention the nature of goods, currently goods under general shipment product is expected
         * @example
         * "HOUSEHOLD GOODS & PERSONAL EFFECTS"
         */
        natureOfGoods?: string;
        /**
         * Product code of the shipment is expected, which is a mandatory field and currently GCR (General shipment) only is configured
         */
        productCode: string;
        /**
         * Service code of the shipement
         */
        serviceCode?: string;
        /**
         * Special Handling Codes of the shipments are expected as comma seperated values. SPX, GCR etc
         */
        specialHandlingCodes: IATA.HandlingCode[];
        /**
         * Density group of the shipement
         */
        densityGroup?: string;
      };
      /**
       * Rate reference Id and rate type details to be mentioned. All fields are optional depending on customer requirements.
       * @example
       * {
       *   "currencyCode": "EUR",
       *   "pricingCode": "",
       *   "rateReferenceId": "",
       *   "rateType": ""
       * }
       */
      ratePreference?: {
        /**
         * Currency code of the shipment
         * @example
         * "QAR"
         */
        currencyCode?: string;
        /**
         * Promotion code of the shipment if available
         */
        pricingCode?: string;
        /**
         * Reference ID for rate details
         */
        rateReferenceId?: string;
        /**
         * Type of rate whether it is TACT/STREET/CUSTOMER/CONTRACT/PROMO etc
         */
        rateType?: string;
        /**
         * Prepaid or Collect indicates with P or C
         */
        mopFreightCharges?: string;
        /**
         * Prepaid or Collect indicates with P or C
         */
        mopOtherCharges?: string;
        /**
         * Allotment code for the agent if any
         * @example
         * "FRAALOKUNBOGL1"
         */
        allotmentCode?: string;
      };
      /**
       * Routing details of the booking flight to be mentioned. Here fetchAvailForNextXDays is mandatory field which indicates how many number of days system need to check for from the given deperture days. For example if departure date is mentioned as 01-01-2022 and fetchAvailForNextDays is mentioned as 3, system will revert with flight availability for 01-01-2022,02-01-2022 and 03-01-2022
       * @example
       * {
       *   "directFlight": false,
       *   "excludeTrucking": false,
       *   "fetchAvailForNextXDays": 3,
       *   "noOfTransits": 1,
       *   "operationTypes": [
       *     "CD",
       *     "AB"
       *   ],
       *   "scheduledArrivalDate": "03-Jan-2022",
       *   "scheduledDepartureDate": "02-Jan-2022"
       * }
       */
      routingPreference?: {
        /**
         * Indicates whether direct flight is required (True/False)
         */
        directFlight?: boolean;
        /**
         * Indicates whether to exclude trucking sectors (True/False)
         */
        excludeTrucking?: boolean;
        /**
         * To fetch available schedules for next configured days. Expected Number characters here which is mandatory field.  For eg- if 2 is given then CROAMIS will respond with flight details of the departure date and one more day next to the departure date
         */
        fetchAvailForNextXDays: number;
        /**
         * Number of transits expected between Origin and Destination, can be indicated with number. Eg- 0/1/2/3
         */
        noOfTransits?: number;
        /**
         * Operation type of the flight to be mentioned whether it is PAX/FRT/TRK
         */
        operationTypes?: string[];
        /**
         * Planned scheduled arrival date of the shipment
         */
        scheduledArrivalDate?: string;
        /**
         * Planned scheduled departure date of the shipment, which is a mandatory field
         */
        scheduledDepartureDate: string;
      };
      /**
       * Shipment dimensions to be mentioned and dimensionUnit is conditional mandatory field. In case, Dimension details are provided, then it is mandatory to provide data for all the elements in this section. This section is either fully optional or fully mandatory
       * @example
       * [
       *   {
       *     "dimensionsUnit": "I",
       *     "height": 50,
       *     "length": 40,
       *     "numberOfPieces": 5,
       *     "stackable": true,
       *     "turnable": false,
       *     "toploadable": true,
       *     "weightPerPiece": 100,
       *     "weightUnit": "Kg",
       *     "width": 50,
       *     "volume": 1.02
       *   }
       * ]
       */
      shipmentDimensions: {
        /**
         * C for centimeters and I for inches
         */
        dimensionsUnit?: string;
        height?: number;
        length?: number;
        numberOfPieces?: number;
        /**
         * Whether the shipment is stackable
         */
        stackable?: boolean;
        /**
         * Whether the shipment is turnable
         */
        turnable?: boolean;
        /**
         * Whether the shipment is top loadable
         */
        toploadable?: boolean;
        weightPerPiece?: number;
        weightUnit?: string;
        width?: number;
        volume?: number;
      }[];
      /**
       * Shipment quantity details to be mentioned including piece, weight and volume with units.
       * @example
       * {
       *   "chargeableWeight": 400,
       *   "chargeableWeightUnit": "Kg",
       *   "totalPieces": 1,
       *   "totalVolume": 1,
       *   "totalWeight": 400,
       *   "volumeUnit": "MC",
       *   "weightUnit": "Kg"
       * }
       */
      shipmentQuantityDetails?: {
        /**
         * Shipment total chargeable weight
         */
        chargeableWeight: number;
        /**
         * Weight unit
         * @example
         * "KG"
         */
        chargeableWeightUnit: string;
        totalPieces: number;
        totalVolume: number;
        totalWeight: number;
        volumeUnit: string;
        weightUnit: string;
      };
      /**
       * ULD details need to be mentioned including the ULD position, quantity details and Uld number
       * @example
       * [
       *   {
       *     "loadingInd": "L",
       *     "noOfUlds": 2,
       *     "ownerCode": "QR",
       *     "serialNumber": 1234,
       *     "slac": 123,
       *     "uldType": "AKE",
       *     "uldVolume": 4,
       *     "uldWeight": 500,
       *     "volumeUnit": "MC",
       *     "weightUnit": "KG",
       *     "contourCode": "CCode"
       *   }
       * ]
       */
      shipmentulds?: {
        /**
         * L for lower deck and M for main deck
         */
        loadingInd: string;
        noOfUlds: number;
        ownerCode: string;
        serialNumber: number | string;
        slac: number | string;
        uldType: string;
        uldVolume: number;
        uldWeight: number;
        volumeUnit: string;
        weightUnit: string;
        contourCode?: string;
      }[];
      additionalAviDtls?: {
        citesCategory?: string;
        purposeOfTransport?: string;
        headCount?: any;
      };
      unidDetails?: {
        unid?: string | number;
        unIdCode?: any;
        shippingName?: string;
        section?: string;
        technicalName?: string;
        pkgGroup?: string;
      }[];
      dataLoggerDetails?: {
        dataLoggerName?: string;
        noOfLoggers?: string | number;
        weightPerLogger?: string;
        weightUnit?: string;
      }[];
      shpULDLoadableVolumeDtls?: {
        uldType?: string;
        countOfUld?: string | number;
        contourCode?: string;
        mixLoadedIndicator?: string;
        loadableVolume?: string | number;
        loadIndicator?: string;
      }[];
      shpDryIceDtls?: {
        noOfPieces?: string | number;
        weightPerPiece?: string | number;
        approvedPkgorUld?: string;
        pkgOrUldUsed?: string;
      }[];
      /**
       * Pharma details are provided in this object
       * @example
       * {
       *   "pharmaReqType": "Passive",
       *   "passivePharmaDtl": {
       *     "tnsptTmpTo": 25,
       *     "tnsptTmpFrm": 25,
       *     "stwgTmpTo": 25,
       *     "stwgTmpFrm": 25,
       *     "prdTmpTo": 25,
       *     "prdTmpFrm": 25
       *   },
       *   "isPassive": true
       * }
       */
      pharmaDtls?: {
        pharmaReqType?: string;
        passivePharmaDtl?: {
          tnsptTmpTo?: number;
          tnsptTmpFrm?: number;
          stwgTmpTo?: number;
          stwgTmpFrm?: number;
          prdTmpTo?: number;
          prdTmpFrm?: number;
        };
        isPassive?: boolean;
      };
      /**
       * Bup Indicator
       * @example
       * "N"
       */
      bupIndicator: string;
      source?: string;
    };
    responses?: {
      /**
       *
       * @example
       * {
       *   "availabilityResponseSOs": [
       *     {
       *       "departureDate": "28-JAN-2022",
       *       "availabilityResponseSOs": [
       *         {
       *           "requestRefId": "971836",
       *           "autoConfirm": true,
       *           "totalTransitTime": "25",
       *           "origin": "FRA",
       *           "destination": "EUH",
       *           "flightItineraries": [
       *             [
       *               {
       *                 "carrierCode": "QR",
       *                 "carrierNumber": "3500",
       *                 "suffix": null,
       *                 "scheduledDepartureDate": "04-DEC-2020",
       *                 "scheduledArrivalDate": "04-DEC-2020",
       *                 "scheduledDepartureTime": "04-Dec-2020 09:00",
       *                 "scheduledArrivalTime": "04-Dec-2020 16:00",
       *                 "segmentOfArrival": "DOH",
       *                 "segmentOfDeparture": "DEL",
       *                 "dow": "Friday",
       *                 "dayChange": "0",
       *                 "allotmentCode": "",
       *                 "bookingStatus": "NN",
       *                 "iataEqpType": "359",
       *                 "carrierAircraftType": "359",
       *                 "tailNumber": null,
       *                 "operationType": "PAX",
       *                 "flightBookingStatus": "Open",
       *                 "shpFltMvtSts": null,
       *                 "flightOperationStatus": null,
       *                 "weight": 600,
       *                 "volume": 2.4,
       *                 "pcs": 12,
       *                 "pendingWeight": null,
       *                 "pendingVolume": null,
       *                 "pendingPcs": null,
       *                 "operationalCarrierCode": null,
       *                 "operationalCarrierNumber": null
       *               },
       *               {
       *                 "carrierCode": "QR",
       *                 "carrierNumber": "0067",
       *                 "suffix": null,
       *                 "scheduledDepartureDate": "05-DEC-2020",
       *                 "scheduledArrivalDate": "05-DEC-2020",
       *                 "scheduledDepartureTime": "05-Dec-2020 08:15",
       *                 "scheduledArrivalTime": "05-Dec-2020 13:00",
       *                 "segmentOfArrival": "FRA",
       *                 "segmentOfDeparture": "DOH",
       *                 "dow": "Saturday",
       *                 "dayChange": "0",
       *                 "allotmentCode": "",
       *                 "bookingStatus": "NN",
       *                 "iataEqpType": "77W",
       *                 "carrierAircraftType": "77P",
       *                 "tailNumber": null,
       *                 "operationType": "PAX",
       *                 "flightBookingStatus": "Open",
       *                 "shpFltMvtSts": null,
       *                 "flightOperationStatus": null,
       *                 "weight": 600,
       *                 "volume": 2.4,
       *                 "pcs": 12,
       *                 "pendingWeight": null,
       *                 "pendingVolume": null,
       *                 "pendingPcs": null,
       *                 "operationalCarrierCode": null,
       *                 "operationalCarrierNumber": null
       *               }
       *             ]
       *           ],
       *           "productCommodityDetails": {
       *             "productCode": "GCR",
       *             "serviceCode": "J",
       *             "commodityCode": 9999,
       *             "natureOfGoods": "GENERAL",
       *             "dgShipment": false,
       *             "caoIndicator": false,
       *             "specialHandlingCodes": [
       *               "GCR"
       *             ],
       *             "densityGroup": ""
       *           },
       *           "rateDetails": {
       *             "currencyCode": "INR",
       *             "pricingCode": "",
       *             "pricingCodeRateApplied": false,
       *             "rateType": "Street Rate",
       *             "chargeableWeight": 600,
       *             "chargeableWeightUnit": "K",
       *             "totalAmount": 433050,
       *             "discountedtotalAmount": 433050,
       *             "discountPercentage": "0%",
       *             "ratePerkilo": 721.75,
       *             "chargesBreakDown": [
       *               {
       *                 "chargeType": "Freight Charges",
       *                 "chargeCode": null,
       *                 "chargeDescription": null,
       *                 "chargeAmount": 432000
       *               },
       *               {
       *                 "chargeType": "OCDC",
       *                 "chargeCode": null,
       *                 "chargeDescription": null,
       *                 "chargeAmount": 1050
       *               }
       *             ],
       *             "contributionNotMet": false,
       *             "promoCode": "",
       *             "prosRateId": "3cd831f3-1702-4d94-8145-66bbff68a2f2",
       *             "offerId": "9fa5e29e-3349-490b-b718-da3d5e56481e",
       *             "pivotWeight": 0,
       *             "overPivotRate": 0,
       *             "overFlowRate": 0
       *           },
       *           "embargoNotificationRemarks": "remarks"
       *         }
       *       ],
       *       "businessErrorSOs": [
       *         {
       *           "additionalMessage": "Requested booking date is beyond Booking window. Please contact local sales",
       *           "addonStatus": "Not validated",
       *           "boardPoint": "",
       *           "reasonCode": "CRO.API.008",
       *           "reasonDesc": "Requested booking date is beyond Booking window. Please contact local sales",
       *           "reasonSummary": "Requested booking date is beyond Booking window",
       *           "validationName": "Invalid Dates"
       *         }
       *       ],
       *       "flightWithExceptions?": [
       *         {
       *           "carrierCode": "QR",
       *           "carrierNumber": "2201",
       *           "scheduledDepDate": "2025-09-13T21:00:00.000+00:00",
       *           "segDep": "FRA",
       *           "segArr": "DOH",
       *           "capStatus": false,
       *           "reason": "Routing Priority 1.0 - reached limit to display in offer. Flight Details QR6822nullFRADOH14-Sep-2025"
       *         }
       *       ]
       *     }
       *   ]
       * }
       */
      /**
       * The request has succeeded without any errors.
       */
      200?: {
        availabilityResponseSOs: {
          departureDate?: string;
          availabilityResponseSOs?: {
            requestRefId?: string;
            autoConfirm?: boolean;
            totalTransitTime?: string;
            origin?: string;
            destination?: string;
            flightItineraries: {
              carrierCode: string;
              carrierNumber: string;
              suffix?: string;
              scheduledDepartureDate: string;
              scheduledArrivalDate?: string;
              scheduledDepartureTime?: string;
              scheduledArrivalTime?: string;
              segmentOfArrival: string;
              segmentOfDeparture: string;
              dow?: string;
              dayChange?: string;
              allotmentCode?: string;
              bookingStatus?: string;
              iataEqpType?: string;
              carrierAircraftType?: string;
              tailNumber?: string;
              operationType?: string;
              flightBookingStatus?: string;
              shpFltMvtSts?: string;
              flightOperationStatus?: string;
              weight?: number;
              volume?: number;
              pcs?: number;
              pendingWeight?: number;
              pendingVolume?: number;
              pendingPcs?: number;
              operationalCarrierCode?: number | string;
              operationalCarrierNumber?: number;
            }[];
            productCommodityDetails?: {
              /**
               * Optional field to indicate whether the shipment is Cargo aircraft only or not. Expected True/False
               */
              caoIndicator?: boolean;
              /**
               * Commodity code of the shipment, currently commodity codes of General shipment is expected
               * @example
               * 9998
               */
              commodityCode: string | number;
              /**
               * To indicate whether the shipment is DG or not. Expected True/False
               */
              dgShipment?: boolean;
              /**
               * To mention the nature of goods, currently goods under general shipment product is expected
               * @example
               * "HOUSEHOLD GOODS & PERSONAL EFFECTS"
               */
              natureOfGoods?: string;
              /**
               * Product code of the shipment is expected, which is a mandatory field and currently GCR (General shipment) only is configured
               */
              productCode: string;
              /**
               * Service code of the shipement
               */
              serviceCode?: string;
              /**
               * Special Handling Codes of the shipments are expected as comma seperated values. SPX, GCR etc
               */
              specialHandlingCodes: IATA.HandlingCode[];
              /**
               * Density group of the shipement
               */
              densityGroup?: string;
            };
            rateDetails?: {
              currencyCode: string;
              pricingCode?: string;
              pricingCodeRateApplied?: boolean;
              rateType: string;
              chargeableWeight: number;
              chargeableWeightUnit: string;
              totalAmount: number;
              discountedtotalAmount: number;
              discountPercentage: string;
              ratePerkilo: number;
              chargesBreakDown?: {
                chargeType?: string;
                chargeCode?: string;
                chargeDescription?: string;
                chargeAmount?: number;
              }[];
              contributionNotMet?: boolean;
              promoCode?: string;
              prosRateId?: string;
              offerId?: string;
              pivotWeight?: number;
              overPivotRate?: number;
              overFlowRate?: number;
            };
            embargoNotificationRemarks?: string;
          }[];
          businessErrorSOs?: {
            additionalMessage?: string;
            addonStatus?: string;
            boardPoint?: string;
            reasonCode?: string;
            reasonDesc?: string;
            reasonSummary?: string;
            validationName?: string;
          }[];
          flightWithExceptions?: {
            carrierCode?: string;
            carrierNumber?: string;
            scheduledDepDate?: string;
            segDep?: string;
            segArr?: string;
            capStatus?: boolean;
            reason?: string;
          }[];
        }[];
      };
      /**
       * Models the standard structure for an error response
       */
      /**
       *
       * @example
       * {
       *   "success": false,
       *   "apiName": "api-name",
       *   "version": "v1",
       *   "correlationId": "3sd3-213dhusa-21313213231",
       *   "timestamp": "2020-08-06T15:49:57.819Z",
       *   "errorDetails": [
       *     {
       *       "code": 404,
       *       "message": "The API has not been implemented",
       *       "additionalInfo": "Not Found : The API has not been implemented"
       *     }
       *   ]
       * }
       */
      /**
       * The origin server did not find a current representation for the target resource or is not willing to disclose that one exists.
       */
      404?: {
        success: boolean;
        apiName: string;
        version: string;
        correlationId: string;
        timestamp: string;
        errorDetails: {
          code: number;
          message: string;
          additionalInfo: string;
        }[];
      };
    };
  };
  /**
   * Shipment booking is the process of requesting to book a cargo for carrying from one point to another point through an aircraft operated by QR.  As part the Booking API , based on the request, QR will perform the various business validations and if successful, will create a booking in QR Cargo System.
   *  */
  export type POST_BOOKING = {
    body?: {
      /**
       * The customer information should be provided. Here agentCassCode and agentIataCode are mandatory fields and   accountNumber is conditionally mandatory only when IATA or CASS not exists. Also the phone number accepts 0-9, Space< >, Plus<+> and Round Bracket<()>,Mobile number allow 0-9, Space< >, Plus<+> and Round Bracket<()>
       * @example
       * {
       *   "accountNumber": "7678522",
       *   "agentCassCode": "6815",
       *   "agentIataCode": "2347041",
       *   "contactDetails": {
       *     "emailAddress": "test@qatarairways.com.qa",
       *     "name": "Nishi Sahoo",
       *     "phoneNumber": "+9473445435435"
       *   },
       *   "customerName": "XForwarding"
       * }
       */
      customerDetails?: {
        /**
         * The acccount number of the customer will be mentioned. This is conditionally mandatory field and Mandatory only when IATA or CASS not exists
         * @example
         * "accountNumber - 7678522"
         */
        accountNumber?: string;
        /**
         * The agent IATA cass code to be mentioned, only number characters are allowed
         * @example
         * "agentCassCode - 2883"
         */
        agentCassCode?: string | number;
        /**
         * The agent IATA  code to be mentioned, only number characters are allowed
         * @example
         * "agentCassCode - 8324557"
         */
        agentIataCode?: string | number;
        /**
         * Contact details of the customer will be mentioned, including emailAddress, Agent name and phoneNumber
         */
        contactDetails?: {
          emailAddress?: string;
          name?: string;
          phoneNumber?: string;
        };
        /**
         * The agent name to be mentioned. which is an optional field.
         */
        customerName?: string;
      };
      /**
       * Document Details section has Mandatory fields for airWaybillIssuePlace, airWaybillCurrency, airWaybillNumber, airwaybillDestination, airWaybillOrigin, airlinePrefix and documentType
       * @example
       * {
       *   "airWaybillCurrency": "INR",
       *   "airWaybillIssuePlace": "DEL",
       *   "airWaybillNumber": "",
       *   "airWaybilldestination": "FRA",
       *   "airWaybillorigin": "DEL",
       *   "airlinePrefix": 157,
       *   "documentType": "MAWB",
       *   "mopFreightCharges": "P",
       *   "mopOtherCharges": "P"
       * }
       */
      documentDetails?: {
        /**
         * Currency of the AWB
         * @example
         * "QAR"
         */
        airWaybillCurrency: string;
        /**
         * Airway bill issued place
         * @example
         * "DOH"
         */
        airWaybillIssuePlace: string;
        airWaybillNumber: string;
        /**
         * Destination of the AWB
         * @example
         * "LHR"
         */
        airWaybilldestination: string;
        /**
         * Origin of the AWB
         * @example
         * "DOH"
         */
        airWaybillorigin: string;
        airlinePrefix: any;
        /**
         * Whether MAWB/CBV/DN/CN38
         */
        documentType: string;
        /**
         * P for prepaid and C for collect
         */
        mopFreightCharges?: string;
        /**
         * P for prepaid and C for collect
         */
        mopOtherCharges?: string;
      };
      /**
       * The AWB product details to be mentioned here. CommodityCode and ProductCode are mandatory fields
       * @example
       * {
       *   "productCode": "GCR",
       *   "serviceCode": "J",
       *   "commodityCode": 9999,
       *   "natureOfGoods": "GENERAL",
       *   "dgShipment": false,
       *   "caoIndicator": false,
       *   "specialHandlingCodes": [
       *     "GCR"
       *   ],
       *   "densityGroup": ""
       * }
       */
      productCommodityDetails?: {
        /**
         * Optional field to indicate whether the shipment is Cargo aircraft only or not. Expected True/False
         */
        caoIndicator?: boolean;
        /**
         * Commodity code of the shipment, currently commodity codes of General shipment is expected
         * @example
         * 9998
         */
        commodityCode: string | number;
        /**
         * To indicate whether the shipment is DG or not. Expected True/False
         */
        dgShipment?: boolean;
        /**
         * To mention the nature of goods, currently goods under general shipment product is expected
         * @example
         * "HOUSEHOLD GOODS & PERSONAL EFFECTS"
         */
        natureOfGoods?: string;
        /**
         * Product code of the shipment is expected, which is a mandatory field and currently GCR (General shipment) only is configured
         */
        productCode: string;
        /**
         * Service code of the shipement
         */
        serviceCode?: string;
        /**
         * Special Handling Codes of the shipments are expected as comma seperated values. SPX, GCR etc
         */
        specialHandlingCodes: IATA.HandlingCode[];
        /**
         * Density group of the shipement
         */
        densityGroup?: string;
      };
      /**
       * Additional details can be requested including any otherRemarks, otherServiceInformation and specialServiceRequests
       * @example
       * {
       *   "otherRemarks": [
       *     "Inform consignee upon arrival"
       *   ],
       *   "otherServiceInformation": [
       *     "AVI must be feed at evey 2 hours"
       *   ],
       *   "specialServiceRequests": [
       *     "AVI must be feed at evey 2 hours"
       *   ]
       * }
       */
      additionalDetails?: {
        /**
         * If there is any remarks to be captured against the AWB
         */
        otherRemarks?: any[];
        /**
         * Other service information - OSI
         */
        otherServiceInformation?: any[];
        /**
         * Special Service Requests - SSR
         */
        specialServiceRequests?: any[];
      };
      /**
       * Shipment dimensions to be mentioned and dimensionUnit is conditional mandatory field. In case, Dimension details are provided, then it is mandatory to provide data for all the elements in this section. This section is either fully optional or fully mandatory
       * @example
       * [
       *   {
       *     "dimensionsUnit": "I",
       *     "height": 50,
       *     "length": 40,
       *     "numberOfPieces": 5,
       *     "stackable": true,
       *     "turnable": false,
       *     "weightPerPiece": 100,
       *     "weightUnit": "Kg",
       *     "width": 50
       *   }
       * ]
       */
      shipmentDimensions: {
        /**
         * C for centimeters and I for inches
         */
        dimensionsUnit?: string;
        height?: number;
        length?: number;
        numberOfPieces?: number;
        /**
         * Whether the shipment is stackable
         */
        stackable?: boolean;
        /**
         * Whether the shipment is turnable
         */
        turnable?: boolean;
        weightPerPiece?: number;
        weightUnit?: string;
        width?: number;
        volume?: number;
      }[];
      /**
       * Shipment quantity details to be mentioned including piece, weight and volume with units.
       * @example
       * {
       *   "chargeableWeight": 400,
       *   "chargeableWeightUnit": "Kg",
       *   "totalPieces": 1,
       *   "totalVolume": 1,
       *   "totalWeight": 400,
       *   "volumeUnit": "MC",
       *   "weightUnit": "Kg"
       * }
       */
      shipmentQuantityDetails?: {
        /**
         * Shipment total chargeable weight
         */
        chargeableWeight?: number;
        /**
         * Weight unit
         * @example
         * "KG"
         */
        chargeableWeightUnit?: string;
        totalPieces?: number;
        totalVolume?: number;
        totalWeight?: number;
        volumeUnit?: string;
        weightUnit?: string;
      };
      /**
       * Request Reference ID, which is a mandatory field
       */
      requestRefId: string;
      /**
       * 1 for New Booking, 2 for amend booking
       */
      requestType: string;
      /**
       * The booking request flight itinerary details will be shared in the request. Here carrierCode, carrierNumber, scheduledArrivalDate, scheduledDepartureDate,segmentOfArrival, segmentOfDeparture are mandatory fields and suffix is conditionally mandatory and Required to be passed only if the flight details have suffix information.
       * @example
       * [
       *   {
       *     "carrierCode": "QR",
       *     "carrierNumber": "3500",
       *     "suffix": null,
       *     "scheduledDepartureDate": "04-DEC-2020",
       *     "scheduledArrivalDate": "04-DEC-2020",
       *     "scheduledDepartureTime": "04-Dec-2020 09:00",
       *     "scheduledArrivalTime": "04-Dec-2020 16:00",
       *     "segmentOfArrival": "DOH",
       *     "segmentOfDeparture": "DEL",
       *     "dow": "Friday",
       *     "dayChange": "0",
       *     "allotmentCode": "",
       *     "bookingStatus": "NN",
       *     "iataEqpType": "359",
       *     "carrierAircraftType": "359",
       *     "tailNumber": null,
       *     "operationType": "PAX",
       *     "flightBookingStatus": "Open",
       *     "shpFltMvtSts": null,
       *     "flightOperationStatus": null,
       *     "weight": 600,
       *     "volume": 2.4,
       *     "pcs": 12,
       *     "pendingWeight": null,
       *     "pendingVolume": null,
       *     "pendingPcs": null,
       *     "operationalCarrierCode": null,
       *     "operationalCarrierNumber": null
       *   },
       *   {
       *     "carrierCode": "QR",
       *     "carrierNumber": "0067",
       *     "suffix": null,
       *     "scheduledDepartureDate": "05-DEC-2020",
       *     "scheduledArrivalDate": "05-DEC-2020",
       *     "scheduledDepartureTime": "05-Dec-2020 08:15",
       *     "scheduledArrivalTime": "05-Dec-2020 13:00",
       *     "segmentOfArrival": "FRA",
       *     "segmentOfDeparture": "DOH",
       *     "dow": "Saturday",
       *     "dayChange": "0",
       *     "allotmentCode": "",
       *     "bookingStatus": "NN",
       *     "iataEqpType": "77W",
       *     "carrierAircraftType": "77P",
       *     "tailNumber": null,
       *     "operationType": "PAX",
       *     "flightBookingStatus": "Open",
       *     "shpFltMvtSts": null,
       *     "flightOperationStatus": null,
       *     "weight": 600,
       *     "volume": 2.4,
       *     "pcs": 12,
       *     "pendingWeight": null,
       *     "pendingVolume": null,
       *     "pendingPcs": null,
       *     "operationalCarrierCode": null,
       *     "operationalCarrierNumber": null
       *   }
       * ]
       */
      flightItineraries: {
        /**
         * Carrier code in which booking is requested
         * @example
         * "QR"
         */
        carrierCode: string;
        /**
         * Carrier number to be mentioned
         */
        carrierNumber: string;
        /**
         * Conditionally mandatory element, Required to be passed only if the flight details have suffix information
         */
        suffix?: string;
        /**
         * Mandatory field to mention the scheduled departure date
         */
        scheduledDepartureDate: string;
        /**
         * Mandatory field to mention the scheduled arrival date
         */
        scheduledArrivalDate?: string;
        scheduledDepartureTime?: string;
        scheduledArrivalTime?: string;
        /**
         * Arrival station
         */
        segmentOfArrival: string;
        /**
         * Departure station
         */
        segmentOfDeparture: string;
        /**
         * Day of Week
         */
        dow?: string;
        /**
         * Day change
         */
        dayChange?: string;
        /**
         * Allotment code
         * @example
         * "FRAALOKUNBOGL1"
         */
        allotmentCode?: string;
        /**
         * Booking status of the shipment
         */
        bookingStatus?: string;
        /**
         * IATA aircraft type
         * @example
         * "77W"
         */
        iataEqpType?: string;
        /**
         * Carrier aircraft type
         * @example
         * "77P"
         */
        carrierAircraftType?: string;
        /**
         * Tail number
         * @example
         * "A7BXX"
         */
        tailNumber?: string;
        /**
         * Whether PAX/FRT/TRK
         */
        operationType?: string;
        flightBookingStatus?: string;
        shpFltMvtSts?: string;
        flightOperationStatus?: string;
        weight?: number;
        volume?: number;
        pcs?: number;
        pendingWeight?: number;
        pendingVolume?: number;
        pendingPcs?: number;
        operationalCarrierCode?: number | string;
        operationalCarrierNumber?: number;
      }[];
      /**
       * ULD details need to be mentioned including the ULD position, quantity details and Uld number
       * @example
       * [
       *   {
       *     "loadingInd": "L",
       *     "noOfUlds": 2,
       *     "ownerCode": "QR",
       *     "serialNumber": 1234,
       *     "slac": 123,
       *     "uldType": "AKE",
       *     "uldVolume": 4,
       *     "uldWeight": 500,
       *     "volumeUnit": "MC",
       *     "weightUnit": "KG",
       *     "contourCode": "CCode"
       *   }
       * ]
       */
      shipmentulds?: {
        /**
         * L for lower deck and M for main deck
         */
        loadingInd: string;
        noOfUlds: number;
        ownerCode: string;
        serialNumber: number;
        slac: number;
        uldType: string;
        uldVolume: number;
        uldWeight: number;
        volumeUnit: string;
        weightUnit: string;
        contourCode?: string;
      }[];
      /**
       * Pharma details are provided in this object
       * @example
       * [
       *   {
       *     "tnsptTmpTo": 25,
       *     "tnsptTmpFrm": 25,
       *     "stwgTmpTo": 25,
       *     "stwgTmpFrm": 25,
       *     "prdTmpTo": 25,
       *     "prdTmpFrm": 25,
       *     "requestType": "Passive",
       *     "instructions": ""
       *   }
       * ]
       */
      pharmaDetailSOs?: {
        tnsptTmpTo?: number;
        tnsptTmpFrm?: number;
        stwgTmpTo?: number;
        stwgTmpFrm?: number;
        prdTmpTo?: number;
        prdTmpFrm?: number;
        requestType?: string;
        instructions?: string;
      }[];
      /**
       * Consignee Details are provided in this object
       * @example
       * [
       *   {
       *     "cspType": "SHIPPER",
       *     "cspName": "QATAR AIRWAYS",
       *     "cspAccountNum": "CIRTO02",
       *     "cspStreetAddress": "JPNagar",
       *     "cspPlace": "Bangalore",
       *     "cspState": "KA",
       *     "cspCountry": "IN",
       *     "cspPostalCode": "2220",
       *     "cspPOBox": "23432",
       *     "cspContactType": "TL",
       *     "cspContactDetails": "21323435"
       *   },
       *   {
       *     "cspType": "Consignee",
       *     "cspName": "AIRGROUP EXPRESS DBA",
       *     "cspAccountNum": "24312343",
       *     "cspStreetAddress": "BTM",
       *     "cspPlace": "Chenai",
       *     "cspState": "TN",
       *     "cspCountry": "IN",
       *     "cspPostalCode": "5678",
       *     "cspPOBox": "34567",
       *     "cspContactType": "TL",
       *     "cspContactDetails": "5435435365"
       *   }
       * ]
       */
      shipperConsigneeDetails?: {
        cspType?: string;
        cspName?: string;
        cspAccountNum?: string;
        cspStreetAddress?: string;
        cspPlace?: string;
        cspState?: string;
        cspCountry?: string;
        cspPostalCode?: string;
        cspPOBox?: string;
        cspContactType?: string;
        cspContactDetails?: string;
      }[];
      /**
       * Shipment Notifications details are added
       * @example
       * [
       *   {
       *     "addressType": "",
       *     "address": "",
       *     "milestones": [
       *       {
       *         "mileStoneCode": "",
       *         "mileStoneDescription": ""
       *       }
       *     ]
       *   }
       * ]
       */
      shipmentNotificationSOs?: {
        addressType?: string;
        address?: string;
        milestones?: {
          mileStoneCode?: string;
          mileStoneDescription?: string;
        }[];
      }[];
      shpDryIceDtls?: {
        noOfPieces?: string | number;
        weightPerPiece?: string | number;
        approvedPkgorUld?: string;
        pkgOrUldUsed?: string;
      }[];
      dataLoggerDetails?: {
        dataLoggerName?: string;
        noOfLoggers?: string | number;
        weightPerLogger?: string;
        weightUnit?: string;
      }[];
      /**
       * attachment details were added
       * @example
       * [
       *   {
       *     "documentCategory": "Goods Declarations",
       *     "documentFileReference": null,
       *     "documentSubCategory": "Export Goods Declaration",
       *     "name": "BookingUpdateSO_Json_Schema.docx",
       *     "fileId": "73e435e574776877bd4b44c91205c350",
       *     "size": "19246",
       *     "type": "docx",
       *     "uploadDate": "06-SEP-2021",
       *     "uploadedBy": "76383"
       *   }
       * ]
       */
      attachmentDtls?: {
        documentCategory?: string;
        documentFileReference?: string;
        documentSubCategory?: string;
        name?: string;
        fileId?: string;
        size?: string;
        type?: string;
        uploadDate?: string;
        uploadedBy?: string;
      }[];
      source?: string;
      /**
       * It is optional to send the “callBackURL” element. callBackURL details provided in the booking request will not be considered because QR will consider only the static callback URL configured in QR cargo side (received from customer in ICD) and all the post shipment updates will be sent to customer through that static URL only
       */
      callBackURL?: string;
    };
    responses?: {
      /**
       *
       * @example
       * {
       *   "shipmentReferenceNum": "2630058",
       *   "currentBookingStatus": "Queued for Manual Action",
       *   "bookingCaptured": true,
       *   "customerDetails": {
       *     "accountNumber": "7678522",
       *     "agentCassCode": "6815",
       *     "agentIataCode": "2347041",
       *     "contactDetails": {
       *       "emailAddress": "test@qatarairways.com.qa",
       *       "name": "Nishi Sahoo",
       *       "phoneNumber": "+9473445435435"
       *     },
       *     "customerName": "XForwarding"
       *   },
       *   "documentDetails": {
       *     "airWaybillCurrency": "INR",
       *     "airWaybillIssuePlace": "DEL",
       *     "airWaybillNumber": "",
       *     "airWaybilldestination": "FRA",
       *     "airWaybillorigin": "DEL",
       *     "airlinePrefix": 157,
       *     "documentType": "MAWB",
       *     "mopFreightCharges": "P",
       *     "mopOtherCharges": "P"
       *   },
       *   "flightItineraries": [
       *     {
       *       "carrierCode": "QR",
       *       "carrierNumber": "3500",
       *       "suffix": null,
       *       "scheduledDepartureDate": "04-DEC-2020",
       *       "scheduledArrivalDate": "04-DEC-2020",
       *       "scheduledDepartureTime": "04-Dec-2020 09:00",
       *       "scheduledArrivalTime": "04-Dec-2020 16:00",
       *       "segmentOfArrival": "DOH",
       *       "segmentOfDeparture": "DEL",
       *       "dow": "Friday",
       *       "dayChange": "0",
       *       "allotmentCode": "",
       *       "bookingStatus": "NN",
       *       "iataEqpType": "359",
       *       "carrierAircraftType": "359",
       *       "tailNumber": null,
       *       "operationType": "PAX",
       *       "flightBookingStatus": "Open",
       *       "shpFltMvtSts": null,
       *       "flightOperationStatus": null,
       *       "weight": 600,
       *       "volume": 2.4,
       *       "pcs": 12,
       *       "pendingWeight": null,
       *       "pendingVolume": null,
       *       "pendingPcs": null,
       *       "operationalCarrierCode": null,
       *       "operationalCarrierNumber": null
       *     },
       *     {
       *       "carrierCode": "QR",
       *       "carrierNumber": "0067",
       *       "suffix": null,
       *       "scheduledDepartureDate": "05-DEC-2020",
       *       "scheduledArrivalDate": "05-DEC-2020",
       *       "scheduledDepartureTime": "05-Dec-2020 08:15",
       *       "scheduledArrivalTime": "05-Dec-2020 13:00",
       *       "segmentOfArrival": "FRA",
       *       "segmentOfDeparture": "DOH",
       *       "dow": "Saturday",
       *       "dayChange": "0",
       *       "allotmentCode": "",
       *       "bookingStatus": "NN",
       *       "iataEqpType": "77W",
       *       "carrierAircraftType": "77P",
       *       "tailNumber": null,
       *       "operationType": "PAX",
       *       "flightBookingStatus": "Open",
       *       "shpFltMvtSts": null,
       *       "flightOperationStatus": null,
       *       "weight": 600,
       *       "volume": 2.4,
       *       "pcs": 12,
       *       "pendingWeight": null,
       *       "pendingVolume": null,
       *       "pendingPcs": null,
       *       "operationalCarrierCode": null,
       *       "operationalCarrierNumber": null
       *     }
       *   ],
       *   "productCommodityDetails": {
       *     "productCode": "GCR",
       *     "serviceCode": "J",
       *     "commodityCode": 9999,
       *     "natureOfGoods": "GENERAL",
       *     "dgShipment": false,
       *     "caoIndicator": false,
       *     "specialHandlingCodes": [
       *       "GCR"
       *     ],
       *     "densityGroup": ""
       *   },
       *   "shipmentDimensions": [
       *     {
       *       "dimensionsUnit": "I",
       *       "height": 50,
       *       "length": 40,
       *       "numberOfPieces": 5,
       *       "stackable": true,
       *       "turnable": false,
       *       "weightPerPiece": 100,
       *       "weightUnit": "Kg",
       *       "width": 50
       *     }
       *   ],
       *   "shipmentQuantityDetails": {
       *     "chargeableWeight": 400,
       *     "chargeableWeightUnit": "Kg",
       *     "totalPieces": 1,
       *     "totalVolume": 1,
       *     "totalWeight": 400,
       *     "volumeUnit": "MC",
       *     "weightUnit": "Kg"
       *   },
       *   "shipmentulds": [
       *     {
       *       "loadingInd": "L",
       *       "noOfUlds": 2,
       *       "ownerCode": "QR",
       *       "serialNumber": 1234,
       *       "slac": 123,
       *       "uldType": "AKE",
       *       "uldVolume": 4,
       *       "uldWeight": 500,
       *       "volumeUnit": "MC",
       *       "weightUnit": "KG",
       *       "contourCode": "CCode"
       *     }
       *   ],
       *   "source": "XYZ",
       *   "callBackURL": "https://callback.com",
       *   "additionalDetails": {
       *     "otherRemarks": [
       *       "Inform consignee upon arrival"
       *     ],
       *     "otherServiceInformation": [
       *       "AVI must be feed at evey 2 hours"
       *     ],
       *     "specialServiceRequests": [
       *       "AVI must be feed at evey 2 hours"
       *     ]
       *   },
       *   "rateDetails": {
       *     "currencyCode": "INR",
       *     "rateType": "Street Rate",
       *     "chargeableWeight": 600,
       *     "chargeableWeightUnit": "K",
       *     "totalAmount": 433050,
       *     "discountedtotalAmount": 433050,
       *     "discountPercentage": "0%",
       *     "ratePerkilo": 721.75,
       *     "chargesBreakDown": [
       *       {
       *         "chargeType": "Freight Charges",
       *         "chargeCode": null,
       *         "chargeDescription": null,
       *         "chargeAmount": 432000
       *       },
       *       {
       *         "chargeType": "OCDC",
       *         "chargeCode": null,
       *         "chargeDescription": null,
       *         "chargeAmount": 1050
       *       }
       *     ]
       *   },
       *   "businessErrors": []
       * }
       */
      /**
       * The request has succeeded without any errors.
       */
      200?: {
        shipmentReferenceNum: string;
        currentBookingStatus: string;
        bookingCaptured: boolean;
        customerDetails?: {
          /**
           * The acccount number of the customer will be mentioned. This is conditionally mandatory field and Mandatory only when IATA or CASS not exists
           * @example
           * "accountNumber - 7678522"
           */
          accountNumber?: string;
          /**
           * The agent IATA cass code to be mentioned, only number characters are allowed
           * @example
           * "agentCassCode - 2883"
           */
          agentCassCode?: string | number;
          /**
           * The agent IATA  code to be mentioned, only number characters are allowed
           * @example
           * "agentCassCode - 8324557"
           */
          agentIataCode?: string | number;
          /**
           * Contact details of the customer will be mentioned, including emailAddress, Agent name and phoneNumber
           */
          contactDetails?: {
            emailAddress?: string;
            name?: string;
            phoneNumber?: string;
          };
          /**
           * The agent name to be mentioned. which is an optional field.
           */
          customerName?: string;
        };
        documentDetails?: {
          /**
           * Currency of the AWB
           * @example
           * "QAR"
           */
          airWaybillCurrency: string;
          /**
           * Airway bill issued place
           * @example
           * "DOH"
           */
          airWaybillIssuePlace?: string;
          airWaybillNumber: string;
          /**
           * Destination of the AWB
           * @example
           * "LHR"
           */
          airWaybilldestination: string;
          /**
           * Origin of the AWB
           * @example
           * "DOH"
           */
          airWaybillorigin: string;
          airlinePrefix: any;
          /**
           * Whether MAWB/CBV/DN/CN38
           */
          documentType: string;
          /**
           * P for prepaid and C for collect
           */
          mopFreightCharges?: string;
          /**
           * P for prepaid and C for collect
           */
          mopOtherCharges?: string;
          /**
           * Latest acceptance time for the shipment
           * @example
           * "04-DEC-2020 06:00"
           */
          latTime?: string;
        };
        flightItineraries: {
          /**
           * Carrier code in which booking is requested
           * @example
           * "QR"
           */
          carrierCode: string;
          /**
           * Carrier number to be mentioned
           */
          carrierNumber: string;
          /**
           * Conditionally mandatory element, Required to be passed only if the flight details have suffix information
           */
          suffix?: string;
          /**
           * Mandatory field to mention the scheduled departure date
           */
          scheduledDepartureDate: string;
          /**
           * Mandatory field to mention the scheduled arrival date
           */
          scheduledArrivalDate?: string;
          scheduledDepartureTime?: string;
          scheduledArrivalTime?: string;
          /**
           * Arrival station
           */
          segmentOfArrival: string;
          /**
           * Departure station
           */
          segmentOfDeparture: string;
          /**
           * Day of Week
           */
          dow?: string;
          /**
           * Day change
           */
          dayChange?: string;
          /**
           * Allotment code
           * @example
           * "FRAALOKUNBOGL1"
           */
          allotmentCode?: string;
          /**
           * Booking status of the shipment
           */
          bookingStatus?: string;
          /**
           * IATA aircraft type
           * @example
           * "77W"
           */
          iataEqpType?: string;
          /**
           * Carrier aircraft type
           * @example
           * "77P"
           */
          carrierAircraftType?: string;
          /**
           * Tail number
           * @example
           * "A7BXX"
           */
          tailNumber?: string;
          /**
           * Whether PAX/FRT/TRK
           */
          operationType?: string;
          flightBookingStatus?: string;
          shpFltMvtSts?: string;
          flightOperationStatus?: string;
          weight?: number;
          volume?: number;
          pcs?: number;
          pendingWeight?: number;
          pendingVolume?: number;
          pendingPcs?: number;
          operationalCarrierCode?: number | string;
          operationalCarrierNumber?: number;
        }[];
        productCommodityDetails?: {
          /**
           * Optional field to indicate whether the shipment is Cargo aircraft only or not. Expected True/False
           */
          caoIndicator?: boolean;
          /**
           * Commodity code of the shipment, currently commodity codes of General shipment is expected
           * @example
           * 9998
           */
          commodityCode: string | number;
          /**
           * To indicate whether the shipment is DG or not. Expected True/False
           */
          dgShipment?: boolean;
          /**
           * To mention the nature of goods, currently goods under general shipment product is expected
           * @example
           * "HOUSEHOLD GOODS & PERSONAL EFFECTS"
           */
          natureOfGoods?: string;
          /**
           * Product code of the shipment is expected, which is a mandatory field and currently GCR (General shipment) only is configured
           */
          productCode: string;
          /**
           * Service code of the shipement
           */
          serviceCode?: string;
          /**
           * Special Handling Codes of the shipments are expected as comma seperated values. SPX, GCR etc
           */
          specialHandlingCodes: IATA.HandlingCode[];
          /**
           * Density group of the shipement
           */
          densityGroup?: string;
        };
        shipmentDimensions: {
          /**
           * C for centimeters and I for inches
           */
          dimensionsUnit?: string;
          height?: number;
          length?: number;
          numberOfPieces?: number;
          /**
           * Whether the shipment is stackable
           */
          stackable?: boolean;
          /**
           * Whether the shipment is turnable
           */
          turnable?: boolean;
          weightPerPiece?: number;
          weightUnit?: string;
          width?: number;
          volume?: number;
        }[];
        shipmentQuantityDetails?: {
          /**
           * Shipment total chargeable weight
           */
          chargeableWeight?: number;
          /**
           * Weight unit
           * @example
           * "KG"
           */
          chargeableWeightUnit?: string;
          totalPieces?: number;
          totalVolume?: number;
          totalWeight?: number;
          volumeUnit?: string;
          weightUnit?: string;
        };
        shipmentulds?: {
          /**
           * L for lower deck and M for main deck
           */
          loadingInd: string;
          noOfUlds: number;
          ownerCode: string;
          serialNumber: number;
          slac: number;
          uldType: string;
          uldVolume: number;
          uldWeight: number;
          volumeUnit: string;
          weightUnit: string;
          contourCode?: string;
        }[];
        source?: string;
        callBackURL?: string;
        additionalDetails?: {
          /**
           * If there is any remarks to be captured against the AWB
           */
          otherRemarks?: any[];
          /**
           * Other service information - OSI
           */
          otherServiceInformation?: any[];
          /**
           * Special Service Requests - SSR
           */
          specialServiceRequests?: any[];
        };
        rateDetails?: {
          currencyCode: string;
          rateType: string;
          chargeableWeight: number;
          chargeableWeightUnit: string;
          totalAmount: number;
          discountedtotalAmount: number;
          discountPercentage: string;
          ratePerkilo: number;
          chargesBreakDown?: {
            chargeType?: string;
            chargeCode?: string;
            chargeDescription?: string;
            chargeAmount?: number;
          }[];
        };
        // QR is inconsistent about this field's name: the booking response example uses
        // `businessErrors`, the field has historically been typed as `businessErrorsSs`, and the
        // live inbound webhook payload omits it entirely. Keep both, optional.
        businessErrors?: {
          errorDescription?: string;
          errorType?: string;
        }[];
        businessErrorsSs?: {
          errorDescription?: string;
          errorType?: string;
        }[];
      };
      /**
       * Models the standard structure for an error response
       */
      /**
       *
       * @example
       * {
       *   "success": false,
       *   "apiName": "api-name",
       *   "version": "v1",
       *   "correlationId": "3sd3-213dhusa-21313213231",
       *   "timestamp": "2020-08-06T15:49:57.819Z",
       *   "errorDetails": [
       *     {
       *       "code": 404,
       *       "message": "The API has not been implemented",
       *       "additionalInfo": "Not Found : The API has not been implemented"
       *     }
       *   ]
       * }
       */
      /**
       * The origin server did not find a current representation for the target resource or is not willing to disclose that one exists.
       */
      404?: {
        success: boolean;
        apiName: string;
        version: string;
        correlationId: string;
        timestamp: string;
        errorDetails: {
          code: number;
          message: string;
          additionalInfo: string;
        }[];
      };
    };
  };
  /**
   * Booking Cancellation API service is used for cancelling the active bookings after validating the information. Please note that there is no re-confirmation with user on cancellation requests. Based on the request, the booking will be cancelled in the QR System
   *  */
  export type DELETE_BOOKING = {
    body?: {
      /**
       * The Shipment reference number to be mentioned. Which is a mandatory field
       */
      shipmentReferenceNum: string;
      /**
       * The reason for cancellation to be mentioned
       * @example
       * "Shipment Damaged"
       */
      cancellationReason: string;
      /**
       * The customer information should be provided. Here agentCassCode and agentIataCode are mandatory fields and   accountNumber is conditionally mandatory only when IATA or CASS not exists. Also the phone number accepts 0-9, Space< >, Plus<+> and Round Bracket<()>,Mobile number allow 0-9, Space< >, Plus<+> and Round Bracket<()>
       * @example
       * {
       *   "accountNumber": "7678522",
       *   "agentCassCode": "6815",
       *   "agentIataCode": "2347041",
       *   "contactDetails": {
       *     "emailAddress": "test@qatarairways.com.qa",
       *     "name": "Nishi Sahoo",
       *     "phoneNumber": "+9473445435435"
       *   },
       *   "customerName": "XForwarding"
       * }
       */
      customerDetails?: {
        /**
         * The acccount number of the customer will be mentioned. This is conditionally mandatory field and Mandatory only when IATA or CASS not exists
         * @example
         * "accountNumber - 7678522"
         */
        accountNumber?: string;
        /**
         * The agent IATA cass code to be mentioned, only number characters are allowed
         * @example
         * "agentCassCode - 2883"
         */
        agentCassCode?: string | number;
        /**
         * The agent IATA  code to be mentioned, only number characters are allowed
         * @example
         * "agentCassCode - 8324557"
         */
        agentIataCode?: string | number;
        /**
         * Contact details of the customer will be mentioned, including emailAddress, Agent name and phoneNumber
         */
        contactDetails?: {
          emailAddress?: string;
          name?: string;
          phoneNumber?: string;
        };
        /**
         * The agent name to be mentioned. which is an optional field.
         */
        customerName?: string;
      };
      /**
       * Document Details section has Mandatory fields for airWaybillIssuePlace, airWaybillCurrency, airWaybillNumber, airwaybillDestination, airWaybillOrigin, airlinePrefix and documentType
       * @example
       * {
       *   "airWaybillIssuePlace": "DEL",
       *   "airWaybillNumber": "ABC",
       *   "airWaybilldestination": "FRA",
       *   "airWaybillorigin": "DEL",
       *   "airlinePrefix": 157,
       *   "documentType": "MAWB"
       * }
       */
      documentDetails?: {
        airWaybillIssuePlace?: string;
        airWaybillNumber: string;
        airWaybilldestination: string;
        airWaybillorigin: string;
        airlinePrefix: any;
        documentType: string;
      };
    };
    responses?: {
      /**
       *
       * @example
       * {
       *   "documentDetails": {
       *     "airWaybillIssuePlace": "DEL",
       *     "airWaybillNumber": "ABC",
       *     "airWaybilldestination": "FRA",
       *     "airWaybillorigin": "DEL",
       *     "airlinePrefix": 157,
       *     "documentType": "MAWB"
       *   },
       *   "cancellationRmks": "Booking cancelled Successfully",
       *   "cancellationStatus": "Cancelled"
       * }
       */
      /**
       * The request has succeeded without any errors.
       */
      200?: {
        documentDetails?: {
          airWaybillIssuePlace?: string;
          airWaybillNumber: string;
          airWaybilldestination: string;
          airWaybillorigin: string;
          airlinePrefix: any;
          documentType: string;
        };
        cancellationRmks: string;
        cancellationStatus: string;
      };
      /**
       * Models the standard structure for an error response
       */
      /**
       *
       * @example
       * {
       *   "success": false,
       *   "apiName": "api-name",
       *   "version": "v1",
       *   "correlationId": "3sd3-213dhusa-21313213231",
       *   "timestamp": "2020-08-06T15:49:57.819Z",
       *   "errorDetails": [
       *     {
       *       "code": 404,
       *       "message": "The API has not been implemented",
       *       "additionalInfo": "Not Found : The API has not been implemented"
       *     }
       *   ]
       * }
       */
      /**
       * The origin server did not find a current representation for the target resource or is not willing to disclose that one exists.
       */
      404?: {
        success: boolean;
        apiName: string;
        version: string;
        correlationId: string;
        timestamp: string;
        errorDetails: {
          code: number;
          message: string;
          additionalInfo: string;
        }[];
      };
    };
  };
  /**
   * Gets the daily flight schedules
   *  */
  export type POST_FLIGHT_SCHEDULES_DAILY = {
    body?: {
      origin: string;
      destination: string;
      paxChecked: string;
      rfsChecked: string;
      frtChecked: string;
      fromDate: string;
      toDate: string;
      volume: string | number;
      weight: string | number;
      weightUnit: string;
      overriddenServiceCode: string | number;
      modeOfTransport: string[];
    };
    responses?: {
      /**
       *
       * @example
       * {
       *   "responseList": [
       *     {
       *       "carrierCode": "QR",
       *       "carrierNumber": "0579",
       *       "dateOfDeparture": "25-FEB-2022",
       *       "segment": "DEL-DOH",
       *       "weight": null,
       *       "volume": null,
       *       "aircraft": "77P",
       *       "std": "03:40",
       *       "sta": "05:30(+0)",
       *       "operationType": "PAX",
       *       "dateOfArrival": "25-FEB-2022"
       *     }
       *   ]
       * }
       */
      /**
       * The request has succeeded without any errors.
       */
      200?: {
        responseList: {
          carrierCode: string;
          carrierNumber: string;
          dateOfDeparture: string;
          segment: string;
          weight?: string | number;
          volume?: string | number;
          aircraft: string;
          std: string;
          sta: string;
          operationType: string;
          dateOfArrival: string;
        }[];
      };
      /**
       * Models the standard structure for an error response
       */
      /**
       *
       * @example
       * {
       *   "success": false,
       *   "apiName": "api-name",
       *   "version": "v1",
       *   "correlationId": "3sd3-213dhusa-21313213231",
       *   "timestamp": "2020-08-06T15:49:57.819Z",
       *   "errorDetails": [
       *     {
       *       "code": 404,
       *       "message": "The API has not been implemented",
       *       "additionalInfo": "Not Found : The API has not been implemented"
       *     }
       *   ]
       * }
       */
      /**
       * The origin server did not find a current representation for the target resource or is not willing to disclose that one exists.
       */
      404?: {
        success: boolean;
        apiName: string;
        version: string;
        correlationId: string;
        timestamp: string;
        errorDetails: {
          code: number;
          message: string;
          additionalInfo: string;
        }[];
      };
    };
  };
  /**
   * Gets the weekly flight schedules
   *  */
  export type POST_FLIGHT_SCHEDULES_WEEKLY = {
    body?: {
      origin: string;
      destination: string;
      periodFrom: string;
      periodTo: string;
    };
    responses?: {
      /**
       *
       * @example
       * {
       *   "availableRoutes": [
       *     {
       *       "routingApts": [
       *         "ABC",
       *         "DEF"
       *       ],
       *       "noOfTransits": 3,
       *       "routeSegmentDetails": [
       *         {
       *           "segmentDeparture": "DEL",
       *           "segmentArrival": "DOH",
       *           "operationalFlights": [
       *             {
       *               "carrierCode": "QR",
       *               "carrierNumber": "3500",
       *               "suffix": null,
       *               "departureHour": 12,
       *               "departureMin": 30,
       *               "arrivalHour": 16,
       *               "arrivalMin": 30,
       *               "iataACType": "Iata",
       *               "carrierACType": "Carrier",
       *               "operationType": "Two shift",
       *               "operationalOnSunday": true,
       *               "operationalOnMonday": true,
       *               "operationalOnTuesday": true,
       *               "operationalOnWednesday": true,
       *               "operationalOnThursday": true,
       *               "operationalOnFriday": true,
       *               "operationalOnSaturday": false
       *             }
       *           ]
       *         }
       *       ]
       *     }
       *   ]
       * }
       */
      /**
       * The request has succeeded without any errors.
       */
      200?: {
        availableRoutes: {
          routingApts: string[];
          noOfTransits: number;
          routeSegmentDetails: {
            segmentDeparture: string;
            segmentArrival: string;
            operationalFlights: {
              carrierCode: string;
              carrierNumber: string;
              departureHour: number;
              departureMin: number;
              arrivalHour: number;
              arrivalMin: number;
              iataACType: string;
              carrierACType: string;
              operationType: string;
              operationalOnSunday: boolean;
              operationalOnMonday: boolean;
              operationalOnTuesday: boolean;
              operationalOnWednesday: boolean;
              operationalOnThursday: boolean;
              operationalOnFriday: boolean;
              operationalOnSaturday: boolean;
            }[];
          }[];
        }[];
      };
      /**
       * Models the standard structure for an error response
       */
      /**
       *
       * @example
       * {
       *   "success": false,
       *   "apiName": "api-name",
       *   "version": "v1",
       *   "correlationId": "3sd3-213dhusa-21313213231",
       *   "timestamp": "2020-08-06T15:49:57.819Z",
       *   "errorDetails": [
       *     {
       *       "code": 404,
       *       "message": "The API has not been implemented",
       *       "additionalInfo": "Not Found : The API has not been implemented"
       *     }
       *   ]
       * }
       */
      /**
       * The origin server did not find a current representation for the target resource or is not willing to disclose that one exists.
       */
      404?: {
        success: boolean;
        apiName: string;
        version: string;
        correlationId: string;
        timestamp: string;
        errorDetails: {
          code: number;
          message: string;
          additionalInfo: string;
        }[];
      };
    };
  };
  /**
   * The new API should invoke next available stock assigned for the logged in customer/forwarder when initiated a booking request from any sourceBook a shipping request for cargo transportation.
   *  */
  export type POST_STOCK = {
    body?: {
      customerDetails?: {
        agentCassCode: string;
        agentIataCode: string;
      };
      bookingRequestedCity: string;
    };
    responses?: {
      /**
       *
       * @example
       * {
       *   "errorResponseSO": null,
       *   "prefix": "157",
       *   "docNumber": "46078104"
       * }
       */
      /**
       * The request has succeeded without any errors.
       */
      200?: {
        errorResponseSO?: string;
        prefix?: string;
        docNumber?: string;
      };
      /**
       * Models the standard structure for an error response
       */
      /**
       *
       * @example
       * {
       *   "success": false,
       *   "apiName": "api-name",
       *   "version": "v1",
       *   "correlationId": "3sd3-213dhusa-21313213231",
       *   "timestamp": "2020-08-06T15:49:57.819Z",
       *   "errorDetails": [
       *     {
       *       "code": 404,
       *       "message": "The API has not been implemented",
       *       "additionalInfo": "Not Found : The API has not been implemented"
       *     }
       *   ]
       * }
       */
      /**
       * The origin server did not find a current representation for the target resource or is not willing to disclose that one exists.
       */
      404?: {
        success: boolean;
        apiName: string;
        version: string;
        correlationId: string;
        timestamp: string;
        errorDetails: {
          code: number;
          message: string;
          additionalInfo: string;
        }[];
      };
    };
  };
  /**
   * Validates the capture the FWB successfully
   *  */
  export type POST_SHIPMENT_FWB_CAPTURE = {
    body?: {
      /**
       * The AWB prefix
       * @example
       * "157"
       */
      awbPrefix: string;
      /**
       * The MAWB number prefix to be mentioned
       */
      awbNumber: string;
      /**
       * The origin
       */
      origin: string;
      /**
       * The destination
       */
      destination: string;
      /**
       * The iata code
       */
      iata_code: string;
      /**
       * The cass code
       */
      cass_code: string;
    };
    responses?: {
      /**
       *
       * @example
       * {
       *   "airwaybillStatusDetails": {
       *     "requestTimeUTC": "2023-04-21T13:36:32Z",
       *     "airwaybillStatus": {
       *       "status": "Success",
       *       "statusDescription": "FWB Captured Successfully"
       *     }
       *   }
       * }
       */
      /**
       * The request has succeeded without any errors.
       */
      200?: {
        airwaybillStatusDetails?: {
          /**
           * The AWB prefix
           * @example
           * "2023-04-21T13:36:32Z"
           */
          requestTimeUTC: string;
          airwaybillStatus?: {
            /**
             * The airwaybillStatus
             */
            status: string;
            /**
             * The airwaybillStatus description
             */
            statusDescription: string;
          };
        };
      };
      /**
       * Models the standard structure for an error response
       */
      /**
       *
       * @example
       * {
       *   "success": false,
       *   "apiName": "api-name",
       *   "version": "v1",
       *   "correlationId": "3sd3-213dhusa-21313213231",
       *   "timestamp": "2020-08-06T15:49:57.819Z",
       *   "errorDetails": [
       *     {
       *       "code": 404,
       *       "message": "The API has not been implemented",
       *       "additionalInfo": "Not Found : The API has not been implemented"
       *     }
       *   ]
       * }
       */
      /**
       * The origin server did not find a current representation for the target resource or is not willing to disclose that one exists.
       */
      404?: {
        success: boolean;
        apiName: string;
        version: string;
        correlationId: string;
        timestamp: string;
        errorDetails: {
          code: number;
          message: string;
          additionalInfo: string;
        }[];
      };
    };
  };
  /**
   * Cargo Tracking Request API service provides the AWB details such as Shipment information, Flight(s) details and movement information. Status tracking API, provides a real time visibility of the shipment movements to the User. So that, Customers can check the shipment status updates instantly and plan accordingly.
   *  */
  export type POST_SHIPMENT_TRACK = {
    body?: {
      cargoTrackingRequestSOs: {
        /**
         * The document type need to be mentioned whether it is MAWB, CBV, CN38, DN or HAWB. Currently only MAWB is supported.
         * @example
         * "MAWB"
         */
        documentType: string;
        /**
         * The MAWB number prefix to be mentioned
         */
        documentPrefix: string;
        /**
         * The document number or MAWB number to be mentioned
         */
        documentNumber: string;
      }[];
    };
    responses?: {
      /**
       *
       * @example
       * {
       *   "cargoTrackingSOs": [
       *     {
       *       "docType": "MAWB",
       *       "docPrefix": "157",
       *       "docNumber": "12345678",
       *       "snrNumber": "22222222",
       *       "volumeUnit": "MC",
       *       "agentCode": "",
       *       "agentName": "",
       *       "depPcs": 0,
       *       "origin": "FRA",
       *       "destination": "PEK",
       *       "pieces": 1,
       *       "weight": 55,
       *       "volume": 0.312,
       *       "shipmentInfo": "MAWB 157-12345678 SNR 22222222 FRA-PEK 1 Pcs 55.0 Kgs 0.312 MC",
       *       "cargoTrackingFlightList": [
       *         {
       *           "flightNumber": "QR-1234 ",
       *           "departedDate": "21-Oct-2021 00:00",
       *           "arrivalDate": "21-Oct-2021 00:00",
       *           "operationType": "PAX",
       *           "segmentOfDeparture": "FRA",
       *           "segmetnOfArrival": "DOH",
       *           "colourSts": "abc",
       *           "depPcs": 0,
       *           "std": "16:53",
       *           "sta": "23:42",
       *           "pieces": 1,
       *           "weight": 55,
       *           "volume": 0.312,
       *           "weightUnit": "",
       *           "volumeUnit": "",
       *           "flightStatus": "Departed",
       *           "bkgStatus": "HK",
       *           "cargoTrackingMvtStausList": ""
       *         },
       *         {
       *           "flightNumber": "QR-4321 ",
       *           "departedDate": "23-Oct-2021 00:00",
       *           "arrivalDate": "24-Oct-2021 00:00",
       *           "operationType": "PAX",
       *           "segmentOfDeparture": "DOH",
       *           "segmetnOfArrival": "PEK",
       *           "colourSts": "",
       *           "depPcs": 0,
       *           "std": "11:46",
       *           "sta": "00:52(+1)",
       *           "pieces": 1,
       *           "weight": 55,
       *           "volume": 0.312,
       *           "weightUnit": "",
       *           "volumeUnit": "",
       *           "flightStatus": "Departed",
       *           "bkgStatus": "HK",
       *           "cargoTrackingMvtStausList": ""
       *         }
       *       ],
       *       "cargoTrackingMvtStausList": [
       *         {
       *           "movementStatus": "NFD",
       *           "cargoTrackingMvtULDSOs": [],
       *           "createdDate": "24-Oct-2021 02:19",
       *           "eventAirport": "PEK",
       *           "eventDate": "24-Oct-2021 07:19",
       *           "shipmentWeight": 55,
       *           "shipmentVolume": 0.31,
       *           "shipmentPieces": 1,
       *           "partOrTotal": "T",
       *           "movementDetails": "Notified consignee 1 Pcs 55 Kgs 24-Oct-2021 07:19 at PEK"
       *         },
       *         {
       *           "movementStatus": "RCF",
       *           "cargoTrackingMvtULDSOs": [],
       *           "createdDate": "23-Oct-2021 23:56",
       *           "eventAirport": "PEK",
       *           "eventDate": "24-Oct-2021 04:56",
       *           "shipmentWeight": 55,
       *           "shipmentVolume": 0.31,
       *           "shipmentPieces": 1,
       *           "partOrTotal": "T",
       *           "movementDetails": "Received cargo from flight On QR 4321/24-Oct-2021 04:56,1 Pcs 55 Kgs 24-Oct-2021 04:56 at PEK"
       *         },
       *         {
       *           "movementStatus": "MAN",
       *           "cargoTrackingMvtULDSOs": [],
       *           "createdDate": "21-Oct-2021 12:50",
       *           "eventAirport": "FRA",
       *           "eventDate": "21-Oct-2021 11:50",
       *           "shipmentWeight": 55,
       *           "shipmentVolume": 0.31,
       *           "shipmentPieces": 1,
       *           "partOrTotal": "T",
       *           "movementDetails": "Manifested On QR 1234/21-Oct-2021 11:50,1 Pcs 55 Kgs 21-Oct-2021 11:50 at FRA"
       *         },
       *         {
       *           "movementStatus": "MAN",
       *           "cargoTrackingMvtULDSOs": [],
       *           "createdDate": "23-Oct-2021 05:57",
       *           "eventAirport": "DOH",
       *           "eventDate": "23-Oct-2021 05:57",
       *           "shipmentWeight": 55,
       *           "shipmentVolume": 0.31,
       *           "shipmentPieces": 1,
       *           "partOrTotal": "T",
       *           "movementDetails": "Manifested On QR 4321/23-Oct-2021 05:57,1 Pcs 55 Kgs 23-Oct-2021 05:57 at DOH"
       *         },
       *         {
       *           "movementStatus": "ARR",
       *           "cargoTrackingMvtULDSOs": [],
       *           "createdDate": "23-Oct-2021 19:58",
       *           "eventAirport": "PEK",
       *           "eventDate": "24-Oct-2021 00:52",
       *           "shipmentWeight": 55,
       *           "shipmentVolume": 0.31,
       *           "shipmentPieces": 1,
       *           "partOrTotal": "T",
       *           "movementDetails": "Flight arrived On QR 4321/24-Oct-2021 00:52,1 Pcs 55 Kgs 24-Oct-2021 00:52 at PEK"
       *         },
       *         {
       *           "movementStatus": "DLV",
       *           "cargoTrackingMvtULDSOs": [],
       *           "createdDate": "27-Oct-2021 10:44",
       *           "eventAirport": "PEK",
       *           "eventDate": "27-Oct-2021 15:47",
       *           "shipmentWeight": 55,
       *           "shipmentVolume": 0.31,
       *           "shipmentPieces": 1,
       *           "partOrTotal": "T",
       *           "movementDetails": "Delivered 1 Pcs 55 Kgs 27-Oct-2021 15:47 at PEK"
       *         },
       *         {
       *           "movementStatus": "DEP",
       *           "cargoTrackingMvtULDSOs": [
       *             {
       *               "uldOwnerCode": "JG",
       *               "uldSerialNumber": "38969",
       *               "uldType": "PMC"
       *             }
       *           ],
       *           "createdDate": "23-Oct-2021 12:00",
       *           "eventAirport": "DOH",
       *           "eventDate": "23-Oct-2021 11:46",
       *           "shipmentWeight": 55,
       *           "shipmentVolume": 0.31,
       *           "shipmentPieces": 1,
       *           "partOrTotal": "T",
       *           "movementDetails": "Departed On QR 4321/23-Oct-2021 11:46,1 Pcs 55 Kgs, PMC38969JG, 23-Oct-2021 11:46 at DOH"
       *         },
       *         {
       *           "movementStatus": "DEP",
       *           "cargoTrackingMvtULDSOs": [
       *             {
       *               "uldOwnerCode": "DS",
       *               "uldSerialNumber": "10428",
       *               "uldType": "PMC"
       *             }
       *           ],
       *           "createdDate": "21-Oct-2021 20:52",
       *           "eventAirport": "FRA",
       *           "eventDate": "21-Oct-2021 16:53",
       *           "shipmentWeight": 55,
       *           "shipmentVolume": 0.31,
       *           "shipmentPieces": 1,
       *           "partOrTotal": "T",
       *           "movementDetails": "Departed On QR 1234/21-Oct-2021 16:53,1 Pcs 55 Kgs, PMC10428DS, 21-Oct-2021 16:53 at FRA"
       *         },
       *         {
       *           "movementStatus": "ARR",
       *           "cargoTrackingMvtULDSOs": [],
       *           "createdDate": "21-Oct-2021 23:59",
       *           "eventAirport": "DOH",
       *           "eventDate": "21-Oct-2021 23:42",
       *           "shipmentWeight": 55,
       *           "shipmentVolume": 0.31,
       *           "shipmentPieces": 1,
       *           "partOrTotal": "T",
       *           "movementDetails": "Flight arrived On QR 1234/21-Oct-2021 23:42,1 Pcs 55 Kgs 21-Oct-2021 23:42 at DOH"
       *         },
       *         {
       *           "movementStatus": "RCF",
       *           "cargoTrackingMvtULDSOs": [],
       *           "createdDate": "22-Oct-2021 04:20",
       *           "eventAirport": "DOH",
       *           "eventDate": "22-Oct-2021 04:20",
       *           "shipmentWeight": 55,
       *           "shipmentVolume": 0.31,
       *           "shipmentPieces": 1,
       *           "partOrTotal": "T",
       *           "movementDetails": "Received cargo from flight On QR 1234/22-Oct-2021 04:20,1 Pcs 55 Kgs 22-Oct-2021 04:20 at DOH"
       *         },
       *         {
       *           "movementStatus": "RCS",
       *           "cargoTrackingMvtULDSOs": [],
       *           "createdDate": "20-Oct-2021 22:33",
       *           "eventAirport": "FRA",
       *           "eventDate": "20-Oct-2021 21:32",
       *           "shipmentWeight": 55,
       *           "shipmentVolume": 0.31,
       *           "shipmentPieces": 1,
       *           "partOrTotal": "T",
       *           "movementDetails": "Received 1 Pcs 55 Kgs 20-Oct-2021 21:32 at FRA"
       *         }
       *       ],
       *       "trackingAptMvtStausSOs": ""
       *     }
       *   ]
       * }
       */
      /**
       * The request has succeeded without any errors.
       */
      200?: {
        cargoTrackingSOs: {
          docType: string;
          docPrefix: string;
          docNumber: string;
          snrNumber: string;
          volumeUnit: string;
          agentCode?: string;
          agentName?: string;
          depPcs: number;
          origin: string;
          destination: string;
          pieces: number;
          weight: number;
          volume: number;
          shipmentInfo: string;
          cargoTrackingFlightList: {
            flightNumber: string;
            departedDate: string;
            segmentOfDeparture: string;
            segmetnOfArrival: string;
            colourSts: string;
            depPcs: number;
            std: string;
            sta: string;
            pieces: number;
            weight: number;
            volume: number;
            weightUnit: string;
            volumeUnit: string;
            flightStatus: string;
          }[];
          cargoTrackingMvtStausList: {
            movementStatus: string;
            cargoTrackingMvtULDSOs: {
              uldOwnerCode: string;
              uldSerialNumber: string;
              uldType: string;
            }[];
            createdDate: string;
            eventAirport: string;
            eventDate: string;
            shipmentWeight: number;
            shipmentVolume: number;
            shipmentPieces: number;
            partOrTotal: string;
            movementDetails: string;
          }[];
          trackingAptMvtStausSOs?: string;
        }[];
      };
      /**
       * Models the standard structure for an error response
       */
      /**
       *
       * @example
       * {
       *   "success": false,
       *   "apiName": "api-name",
       *   "version": "v1",
       *   "correlationId": "3sd3-213dhusa-21313213231",
       *   "timestamp": "2020-08-06T15:49:57.819Z",
       *   "errorDetails": [
       *     {
       *       "code": 404,
       *       "message": "The API has not been implemented",
       *       "additionalInfo": "Not Found : The API has not been implemented"
       *     }
       *   ]
       * }
       */
      /**
       * The origin server did not find a current representation for the target resource or is not willing to disclose that one exists.
       */
      404?: {
        success: boolean;
        apiName: string;
        version: string;
        correlationId: string;
        timestamp: string;
        errorDetails: {
          code: number;
          message: string;
          additionalInfo: string;
        }[];
      };
    };
  };
  /** One QR endpoint as a type map reads it: what we send, and what a 200 carries back. */
  export type Route<TEndpoint extends { body?: unknown; responses?: { 200?: unknown } }> = {
    request: { body: TEndpoint['body'] };
    response: TEndpoint['responses'][200];
  };

  /**
   * Every QR endpoint we call, keyed the way {@link AuthenticatedClient} reads a type map: by method,
   * then by path. QR fronts each of these on its own versioned service prefix, which is the client's
   * `baseUrl` resolver's business — see `QR_SERVICE_PATHS` in `./endpoints.ts`.
   */
  export type Api = {
    GET: {
      '/token': Route<any>;
    };
    POST: {
      '/availability': Route<POST_AVAILABILITY>;
      '/booking': Route<POST_BOOKING>;

      '/stock': Route<POST_STOCK>;
      '/shipment/track': Route<unknown>;
      // Not implemented, and QR exposes no staging equivalent.
      // '/booking/admendment-charges': Route<unknown>;
    };
    DELETE: {
      '/booking': Route<DELETE_BOOKING>;
    };
  };
}
